import { useState, useRef, useEffect } from 'react';

export interface SearchableVaultDropdownProps {
  vaults: Array<{ id: string; name: string; type?: 'personal' | 'shared' | 'business' | string }>;
  value: string | null;
  onChange: (value: string | null) => void;
  allowRoot?: boolean;
  rootLabel?: string;
  style?: React.CSSProperties;
}

export function SearchableVaultDropdown({
  vaults = [],
  value,
  onChange,
  allowRoot = true,
  rootLabel = '📁 Không gian của tôi (Gốc)',
  style = {}
}: SearchableVaultDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Find currently selected label
  let selectedLabel = rootLabel;
  if (value === 'shared') {
    selectedLabel = '📁 Tài liệu được chia sẻ (Tất cả)';
  } else if (value !== null && value !== '') {
    const found = vaults.find(v => v.id === value);
    if (found) {
      const icon = found.type === 'personal' ? '👤' : found.type === 'shared' ? '🤝' : '🏢';
      selectedLabel = `${icon} ${found.name}`;
    } else {
      selectedLabel = `📁 Vault ${value.substring(0, 8)}...`;
    }
  }

  // Filter vaults by search query
  const term = search.toLowerCase().trim();
  const personalVaults = vaults.filter(v => v.type === 'personal' && v.name.toLowerCase().includes(term));
  const sharedVaults = vaults.filter(v => v.type === 'shared' && v.name.toLowerCase().includes(term));
  const businessVaults = vaults.filter(v => (!v.type || v.type === 'business') && v.name.toLowerCase().includes(term));

  const matchRoot = allowRoot && rootLabel.toLowerCase().includes(term);
  const matchSharedRoot = 'tài liệu được chia sẻ'.includes(term);

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-block', minWidth: '260px', ...style }}>
      {/* Trigger Button */}
      <div
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) setSearch('');
        }}
        style={{
          padding: '7px 12px',
          borderRadius: '8px',
          border: '1px solid rgba(56, 189, 248, 0.4)',
          background: '#0f172a',
          color: '#38bdf8',
          fontSize: '13px',
          fontWeight: 500,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
          transition: 'all 0.2s'
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '240px' }}>
          {selectedLabel}
        </span>
        <span style={{ fontSize: '10px', color: '#94a3b8', transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
          ▼
        </span>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            minWidth: '300px',
            background: 'var(--bg-elevated, #1e293b)',
            border: '1px solid rgba(56, 189, 248, 0.5)',
            borderRadius: '12px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.6)',
            zIndex: 9999,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Search Box Header */}
          <div style={{ padding: '10px', background: 'rgba(15, 23, 42, 0.9)', borderBottom: '1px solid rgba(148, 163, 184, 0.2)' }}>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <span style={{ position: 'absolute', left: '10px', fontSize: '13px', color: '#64748b' }}>🔍</span>
              <input
                ref={inputRef}
                type="text"
                placeholder="Tìm kiếm Kho báu theo tên..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 10px 6px 30px',
                  borderRadius: '6px',
                  border: '1px solid rgba(56, 189, 248, 0.3)',
                  background: '#0f172a',
                  color: '#f8fafc',
                  fontSize: '12.5px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
              {search && (
                <span
                  onClick={() => setSearch('')}
                  style={{ position: 'absolute', right: '10px', fontSize: '12px', color: '#94a3b8', cursor: 'pointer' }}
                >
                  ✕
                </span>
              )}
            </div>
          </div>

          {/* Scrollable Vault List */}
          <div
            className="custom-scrollbar"
            style={{
              maxHeight: '260px',
              overflowY: 'auto',
              padding: '6px',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px'
            }}
          >
            {/* My Vault (Personal) Group */}
            {(matchRoot || personalVaults.length > 0) && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', padding: '4px 8px 2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  👤 Kho báu Cá nhân
                </div>
                {matchRoot && (
                  <div
                    onClick={() => { onChange(null); setIsOpen(false); }}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      fontSize: '12.5px',
                      cursor: 'pointer',
                      background: (value === null || value === '') ? 'rgba(56, 189, 248, 0.2)' : 'transparent',
                      color: (value === null || value === '') ? '#38bdf8' : '#e2e8f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <span>{rootLabel}</span>
                    {(value === null || value === '') && <span style={{ fontSize: '11px' }}>✓</span>}
                  </div>
                )}
                {personalVaults.map(v => {
                  const isSel = value === v.id;
                  return (
                    <div
                      key={v.id}
                      onClick={() => { onChange(v.id); setIsOpen(false); }}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '6px',
                        fontSize: '12.5px',
                        cursor: 'pointer',
                        background: isSel ? 'rgba(56, 189, 248, 0.2)' : 'transparent',
                        color: isSel ? '#38bdf8' : '#cbd5e1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📁 {v.name}</span>
                      {isSel && <span style={{ fontSize: '11px', color: '#38bdf8' }}>✓</span>}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Shared with me Group */}
            {(matchSharedRoot || sharedVaults.length > 0) && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '4px' }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', padding: '4px 8px 2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  🤝 Kho báu Được chia sẻ
                </div>
                {matchSharedRoot && (
                  <div
                    onClick={() => { onChange('shared'); setIsOpen(false); }}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      fontSize: '12.5px',
                      cursor: 'pointer',
                      background: value === 'shared' ? 'rgba(56, 189, 248, 0.2)' : 'transparent',
                      color: value === 'shared' ? '#38bdf8' : '#e2e8f0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <span>📁 Tài liệu được chia sẻ</span>
                    {value === 'shared' && <span style={{ fontSize: '11px' }}>✓</span>}
                  </div>
                )}
                {sharedVaults.map(v => {
                  const isSel = value === v.id;
                  return (
                    <div
                      key={v.id}
                      onClick={() => { onChange(v.id); setIsOpen(false); }}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '6px',
                        fontSize: '12.5px',
                        cursor: 'pointer',
                        background: isSel ? 'rgba(56, 189, 248, 0.2)' : 'transparent',
                        color: isSel ? '#38bdf8' : '#cbd5e1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📁 {v.name}</span>
                      {isSel && <span style={{ fontSize: '11px', color: '#38bdf8' }}>✓</span>}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Business Vaults Group */}
            {businessVaults.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '4px' }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', padding: '4px 8px 2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  🏢 Kho báu Doanh nghiệp
                </div>
                {businessVaults.map(v => {
                  const isSel = value === v.id;
                  return (
                    <div
                      key={v.id}
                      onClick={() => { onChange(v.id); setIsOpen(false); }}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '6px',
                        fontSize: '12.5px',
                        cursor: 'pointer',
                        background: isSel ? 'rgba(56, 189, 248, 0.2)' : 'transparent',
                        color: isSel ? '#38bdf8' : '#cbd5e1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📁 {v.name}</span>
                      {isSel && <span style={{ fontSize: '11px', color: '#38bdf8' }}>✓</span>}
                    </div>
                  );
                })}
              </div>
            )}

            {/* No Results found */}
            {!matchRoot && !matchSharedRoot && personalVaults.length === 0 && sharedVaults.length === 0 && businessVaults.length === 0 && (
              <div style={{ padding: '16px 10px', textAlign: 'center', fontSize: '12px', color: '#64748b', fontStyle: 'italic' }}>
                Không tìm thấy Kho báu phù hợp với &quot;{search}&quot;
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
